import {
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useContext, useState } from "react";
import { Edit, Save } from "@mui/icons-material";
import { DataContext } from ".";
import CustomToolbar from "../../utils/customToolbar";
import { useApiClient } from '../../context/ApiClientContext';
import toast from 'react-hot-toast';
import ComponentBackdrop from '../../components/backdrop';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../helper/baseApi';
import SuccessAlert from '../../helper/successAlert';

const Listing = ({ handleShowButton, openEditModal }) => {
  const rows = useContext(DataContext);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const { apiUrls: { mobileApiUrl } } = useApiClient();

  // ✅ Track which row is currently being edited inline
  const [editRowId, setEditRowId] = useState(null);

  // ✅ Store the most recently edited row data
  const [editedRowData, setEditedRowData] = useState(null);

  const onSelectedCheckbox = (id, rows) => {
    let selectedIds = [];

    if (id.type === "include") {
      selectedIds = Array.from(id.ids);
    } else if (id.type === "exclude") {
      const excluded = new Set(id.ids);
      selectedIds = rows
        .filter((row) => !excluded.has(row.id))
        .map((row) => row.id);
    }
    handleShowButton(selectedIds);
  };

  // ✅ Called when inline editing starts
  const handleCellEditStart = (params) => {
    setEditRowId(params.id);
  };

  // ✅ Called when inline editing stops (user presses Enter or clicks away)
  const handleCellEditStop = () => {
    // Don't reset here yet — we'll handle it after save
  };

  // ✅ Capture the edited row's new values from MUI
  const processRowUpdate = (newRow, oldRow) => {
    console.log("Updated Row:", newRow);
    setEditedRowData(newRow);
    return newRow; // must return for DataGrid to update UI
  };

  // ✅ Called when Save button clicked
  const handleSaveRow = async (rowId) => {
    try {
      setOpenBackdrop(true);

      const apiUrl = `${mobileApiUrl}department/update-details`;
      const response = await api.put(apiUrl, {
        departmentId: editedRowData.departmentId,
        name: editedRowData.name,
      });

      if (response.status === 200) {
        setTimeout(() => {
          setOpenBackdrop(false);
          SuccessAlert(response.data.message);
        }, 1000);
      }
    } catch (error) {
      toast.error("Error updating data: " + error.message);
      setOpenBackdrop(false);
    }

    setEditRowId(null);
    setEditedRowData(null);
  };

  const columns = [
    {
      field: "Actions",
      headerName: "Action",
      width: 120,
      type: "actions",
      hideable: false,
      getActions: (params) => {
        // ✅ Show Save button only for the edited row
        if (editRowId === params.row.id) {
          return [
            <IconButton
              key="save"
              onClick={() => handleSaveRow(params.row.id)}
            >
              <Tooltip title="Save" placement="right">
                <Save />
              </Tooltip>
            </IconButton>,
          ];
        }

        // Default edit button
        return [
          <IconButton
            key="edit"
            sx={{ color: "#39493F" }}
            onClick={() => openEditModal(params.row.id)}
          >
            <Tooltip title="Edit" placement="right">
              <Edit />
            </Tooltip>
          </IconButton>,
        ];
      },
    },
    {
      field: "departmentId",
      headerName: "ID",
      width: 60,
    },
    {
      field: "name",
      headerName: "Department",
      editable: true,
      width: 150,
    },
  ];

  return (
    <Box
      style={{
        width: "100%",
        height: "77vh",
        overflowY: rows.length > 10 ? "auto" : "visible",
      }}
    >
      <DataGrid
        columns={columns}
        rows={rows}
        showToolbar
        slots={{ toolbar: CustomToolbar }}
        pageSizeOptions={[50, 100, { value: 500, label: "500" }]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50 },
          },
          density: "compact",
          sorting: { sortModel: [{ field: "departmentId", sort: "desc" }] },
          columns: {
            columnVisibilityModel: { departmentId: false },
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: "No records found" }}
        onCellEditStart={handleCellEditStart}
        onCellEditStop={handleCellEditStop}
        onRowSelectionModelChange={(id) => onSelectedCheckbox(id, rows)}
        processRowUpdate={processRowUpdate} // ✅ Capture updated row data
        experimentalFeatures={{ newEditingApi: true }} // ✅ Required for processRowUpdate
      />

      {/* ✅ Optional: Backdrop loader */}
      <ComponentBackdrop openBackdrop={openBackdrop} />
    </Box>
  );
};

export default Listing;
